import React, { FC, Suspense, useContext, useState } from 'react';
import { queryClient } from 'src/core/root/components/ReactQueryProvider/ReactQueryProvider';
import styles from './BoughtMovies.module.scss';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Balance } from '../../components/Balance';
import { useTranslation } from 'react-i18next';
import { CustomSpinner } from '../../shared/Spinner';
import { useNavigate, useLocation } from 'react-router-dom';
import { PinConfirmationModal } from '../../components/UI/Modals/PinConfirmationModal';
import { OrderModal } from '../../components/UI/Modals/OrderModal';
import { AuthContext } from '../../context/auth';
import { Player } from '../../components/UI/Player';
import ReactPaginate from 'react-paginate';
import { usePurchasedContentQuery, usePurchasedContentStudiosQuery } from './query';
import { URLBoughtMoviesQueryStateModel } from './types';
import { BoughtMoviesModel } from './query/types';
import { BoughtMoviesList } from './components/BoughtMoviesList';
import StreamButton from '../../components/MovieDetailButtons/StreamButton/StreamButton';
import DownloadHDButton from '../../components/MovieDetailButtons/DownloadHDButton/DownloadHDButton';
import BmButton from '../../components/MovieDetailButtons/BmButton/BmButton';

const BoughtMovies: FC = () => {
  const location = useLocation();

  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const authContext = useContext(AuthContext);
  const { isAuthenticated, loading, user, updateUser } = authContext;
  const [playerStatus, setPlayerStatus] = useState<boolean>(false);
  const [orderVisibility, setOrderVisibility] = useState<boolean>(false);
  const [selectedButton, setSelectedButton] = useState<string>('');
  const [selectedPrice, setSelectedPrice] = useState<number>(0);
  const [show, setShow] = useState<boolean>(false);
  const [selectedMovie, setSelectedMovie] = useState<BoughtMoviesModel>();
  const [cfMediaID, setCfMediaID] = useState<string>('');
  const [query, setQuery] = useState<URLBoughtMoviesQueryStateModel>({
    page: 1,
  });
  const [selected, setSelected] = useState<{ label: string; value: number }[]>([]);

  const { boughtMovies, areBoughtMoviesLoaded, boughtMoviesMeta, refetchBoughtMovies } =
    usePurchasedContentQuery(query);
  const { studios, areStudiosLoading } = usePurchasedContentStudiosQuery();

  const formatLabel = (name: string, no: number) => {
    const n = 15;
    const label = name && name?.length > n ? name.substr(0, n - 1) + '...' : name;
    return `${label} (${no})`;
  };

  const renderMenu = (movie: BoughtMoviesModel) => {
    if (!areBoughtMoviesLoaded && movie && isAuthenticated && !loading) {
      return (
        <Col xs={12}>
          <Row>
            {movie &&
              movie.stream &&
              <StreamButton movie={movie}
                            setSelectedButton={setSelectedButton}
                            setSelectedMovie={setSelectedMovie}
                            setOrderVisibility={setOrderVisibility}
                            setSelectedPrice={setSelectedPrice}
                            setShow={setShow}/> }
            {movie &&
              movie.stream &&
              <DownloadHDButton movie={movie}
                                setShow={setShow}
                                setSelectedButton={setSelectedButton}
                                setSelectedMovie={setSelectedMovie}
                                setOrderVisibility={setOrderVisibility}
                                setSelectedPrice={setSelectedPrice}/>}
            {movie &&
              movie.stream &&
              <BmButton movie={movie}/>}
          </Row>
        </Col>
      );
    }
  };
  const handleClose = () => setShow(false);

  const renderStream = () => {
    if (!areBoughtMoviesLoaded && selectedMovie && boughtMovies && isAuthenticated && playerStatus) {
      return (
        <Player banner={selectedMovie.banner} onHide={onHide} id={selectedMovie.id} controls muted src={cfMediaID} />
      );
    }
  };
  const onHide = () => {
    refetchBoughtMovies()
    setPlayerStatus(false);
  };

  if (areBoughtMoviesLoaded || areStudiosLoading) {
    return <CustomSpinner />;
  }

  const handleStudioChange = (values: { label: string; value: number }[]) => {
    setSelected(values);
    const collection = values.map((v) => v.value);
    setQuery({
      ...query,
      studio: collection,
      page:1
    });
  };

  const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQuery({
      ...query,
      sort: e.target.value,
    });
    if (e.target.value === 'new') {
      setQuery({
        ...query,
        sort: 'license.online_start',
      });
    }
    if (e.target.value === 'old') {
      setQuery({
        ...query,
        sort: '-license.online_start',
      });
    }
    if (e.target.value === 'title-a') {
      setQuery({
        ...query,
        sort: 'content_data.title',
      });
    }
    if (e.target.value === 'title-d') {
      setQuery({
        ...query,
        sort: '-content_data.title',
      });
    }
  };
  const withContrast = true;
  const renderPagination = () => {
    if (boughtMovies && boughtMoviesMeta && !areBoughtMoviesLoaded) {
      return (
        <ReactPaginate
          previousLabel={<i className="fa fa-chevron-left" ></i>}
          nextLabel={<i className="fa fa-chevron-right" ></i>}
          breakLabel={'...'}
          breakClassName={styles.breakMe}
          pageCount={boughtMovies && boughtMoviesMeta.last_page}
          marginPagesDisplayed={1}
          pageRangeDisplayed={3}
          onPageChange={handlePageChange}
          forcePage={(query.page as number) - 1}
          containerClassName={'pagination'}
          activeClassName={styles.active}
          previousClassName={query.page === 1 ? `${styles.hideButton}` : `${styles.showButton}`}
          nextClassName={
            boughtMoviesMeta && query.page === boughtMoviesMeta.last_page
              ? `${styles.hideButton}`
              : `${styles.showButton}`
          }
        />
      );
    }
    return null;
  };

  const handlePageChange = (data: { selected: number }) => {
    const selectedPage = data.selected + 1;
    setQuery({
      ...query,
      page: selectedPage,
    });
  };

  const handleSuccessPinConfirmationModal = (purchaseData:any) => {
    if (purchaseData && selectedButton === 'play') {
      setCfMediaID(purchaseData.data.url);
      setPlayerStatus(true);
      setShow(false);
      setOrderVisibility(false);
    } else {
      setShow(false);
      setOrderVisibility(false);
    }
  }

  const handleSuccessOrderModal = (purchaseData:any) => {
    if (purchaseData && selectedButton === 'play') {
      setCfMediaID(purchaseData.data.url);
      setPlayerStatus(true);
    }
  }

  return (
    <Container>
      <Suspense fallback={<CustomSpinner />}>
        <div className={`${styles.pageHeader} ${withContrast ? styles.contrast : ''}`}>
          <Balance />
        </div>
        <h2>{t('boughtMoviesPage.title')}</h2>
        <Row>
          <Col sm={{ span: 9, offset: 3 }}>
            <div className={styles.paginationWrapper}>
              <div className={styles.pagination}>{renderPagination()}</div>
            </div>
          </Col>
        </Row>

        <BoughtMoviesList
          renderMenu={renderMenu}
          boughtMovies={boughtMovies}
          handleStudioChange={handleStudioChange}
          selected={selected}
          formatLabel={formatLabel}
          query={query}
          studios={studios}
          areStudiosLoading={areStudiosLoading}
          handleOptionChange={handleOptionChange}
        />
        <Row>
          <Col sm={{ span: 9, offset: 3 }}>
            <div className={styles.paginationWrapper}>
              <div className={styles.pagination}>{renderPagination()}</div>
            </div>
          </Col>
        </Row>

        <Row>
          <PinConfirmationModal
            key={`pin-modal-${show}`}
            selectedButton={selectedButton}
            handleSuccess={(purchaseData) => handleSuccessPinConfirmationModal(purchaseData)}
            centered={true}
            show={show}
            onHide={handleClose}
            movieId={selectedMovie?.id?.toString() || ''}
            movieTitle={selectedMovie?.title || ''}
          />
          <OrderModal
            key={`order-modal-${orderVisibility}`}
            selectedButton={selectedButton}
            balance={user && (user.balance?.toFixed(2) as string)}
            location={location.pathname}
            price={selectedPrice}
            handleSuccess={(purchaseData) => {
              handleSuccessOrderModal(purchaseData)
            }}
            onRefetch={refetchBoughtMovies}
            centered={true}
            show={orderVisibility}
            onHide={() => setOrderVisibility(false)}
            movieId={selectedMovie?.id?.toString() || ''}
            movieTitle={selectedMovie?.title || ''}
          />
        </Row>
      </Suspense>
      {boughtMovies && cfMediaID && renderStream()}
    </Container>
  );
};

export default BoughtMovies;
